import { useState, useEffect, useRef, useCallback } from 'react';

const useBufferPlaybackVideo = (url) => {
    const [bufferedUrl, setBufferedUrl] = useState(null);
    const [isBuffering, setIsBuffering] = useState(false);
    const [bufferProgress, setBufferProgress] = useState(0);
    const [bufferError, setBufferError] = useState(null);

    const abortControllerRef = useRef(null);
    const previousUrlRef = useRef(null);
    const objectUrlRef = useRef(null);

    const cleanupObjectUrl = useCallback(() => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    const abortFetch = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!url) {
            cleanupObjectUrl();
            setBufferedUrl(null);
            setIsBuffering(false);
            setBufferProgress(0);
            setBufferError(null);
            previousUrlRef.current = null;
            return;
        }

        if (url === previousUrlRef.current) {
            return;
        }

        abortFetch();
        cleanupObjectUrl();

        previousUrlRef.current = url;
        setBufferedUrl(null);
        setIsBuffering(true);
        setBufferProgress(0);
        setBufferError(null);

        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        const bufferVideo = async () => {
            try {
                let fetchUrl = url;
                const fetchOptions = { signal };

                try {
                    const parsedUrl = new URL(url);
                    if (parsedUrl.username || parsedUrl.password) {
                        const credentials = btoa(`${decodeURIComponent(parsedUrl.username)}:${decodeURIComponent(parsedUrl.password)}`);
                        fetchOptions.headers = {
                            Authorization: `Basic ${credentials}`,
                        };
                        parsedUrl.username = '';
                        parsedUrl.password = '';
                        fetchUrl = parsedUrl.toString();
                    }
                } catch (urlParseError) {
                    console.warn('Could not parse URL for credentials extraction:', urlParseError);
                }

                const response = await fetch(fetchUrl, fetchOptions);

                if (!response.ok) {
                    const isCrossOrigin = !url.startsWith(window.location.origin);
                    if (response.status === 404 && isCrossOrigin) {
                        throw new Error(`CORS Error: ${response.status}`);
                    }
                    throw new Error(`${response.status} ${response.statusText}`);
                }

                const contentLength = response.headers.get('content-length');
                const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;

                if (!response.body) {
                    const blob = await response.blob();
                    const objectUrl = URL.createObjectURL(blob);
                    objectUrlRef.current = objectUrl;
                    setBufferedUrl(objectUrl);
                    setBufferProgress(100);
                    setIsBuffering(false);
                    return;
                }

                const reader = response.body.getReader();
                const chunks = [];
                let receivedBytes = 0;

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    chunks.push(value);
                    receivedBytes += value.length;

                    if (totalBytes > 0) {
                        const progress = Math.round((receivedBytes / totalBytes) * 100);
                        setBufferProgress(progress);
                    } else {
                        const estimatedProgress = Math.min(95, Math.round((receivedBytes / (receivedBytes + 100000)) * 100));
                        setBufferProgress(estimatedProgress);
                    }
                }

                const blob = new Blob(chunks, { type: 'video/mp4' });
                const objectUrl = URL.createObjectURL(blob);
                objectUrlRef.current = objectUrl;

                setBufferedUrl(objectUrl);
                setBufferProgress(100);
                setIsBuffering(false);
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }

                console.error('Video buffering error:', error);

                const isCorsError =
                    error.name === 'TypeError' && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'));

                if (isCorsError) {
                    setBufferError('CORS Error: Cross-origin request blocked');
                } else {
                    setBufferError(error.message || 'Failed to buffer video');
                }

                setIsBuffering(false);
                setBufferProgress(0);
            }
        };

        bufferVideo();

        return () => {
            abortFetch();
        };
    }, [url, abortFetch, cleanupObjectUrl]);

    useEffect(() => {
        return () => {
            abortFetch();
            cleanupObjectUrl();
        };
    }, [abortFetch, cleanupObjectUrl]);

    return {
        bufferedUrl,
        isBuffering,
        bufferProgress,
        bufferError,
    };
};

export default useBufferPlaybackVideo;
